import React, { useEffect, useState } from 'react';
import { Plus, X, Trash2, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { formatDate, formatINR } from '../lib/format';
import { waLink, mergeTemplate } from '../lib/whatsapp';
import { TEMPLATES } from '../lib/content';

const CATS = ['photographer','planner','decorator','studio','brand','venue','hotel','agency'];
const STAGES = ['not_contacted','emailed','followup_1','followup_2','replied','call_booked','collaborated','not_interested'];

export default function Outreach() {
  const [items, setItems] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [edit, setEdit] = useState(null);
  const [filter, setFilter] = useState('all');
  const load = () => Promise.all([
    api.get('/outreach').then(r => setItems(r.data.filter(x => !x.deleted_at))),
    api.get('/contacts').then(r => setContacts(r.data.filter(x => !x.deleted_at))),
  ]);
  useEffect(() => { load(); }, []);
  const save = async (o) => {
    if (o.id) await api.put(`/outreach/${o.id}`, o);
    else await api.post('/outreach', o);
    toast.success('Saved'); setEdit(null); load();
  };
  const rm = async (id) => { await api.delete(`/outreach/${id}`); load(); };
  const advance = async (o) => {
    const idx = STAGES.indexOf(o.stage);
    if (idx < STAGES.length - 1) {
      const next = STAGES[idx + 1];
      const followup = new Date(Date.now() + (next === 'emailed' ? 4 : 10) * 86400000).toISOString().slice(0,10);
      await api.put(`/outreach/${o.id}`, { ...o, stage: next, last_contacted: new Date().toISOString().slice(0,10), next_followup: followup });
      load();
    }
  };
  const filtered = filter === 'all' ? items : items.filter(x => x.stage === filter);
  const totalRevenue = items.reduce((s,o) => s + (o.revenue_attributed||0), 0);
  return (
    <div className="px-5 py-5 lg:px-10 lg:py-8 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-5">
        <div><div className="eyebrow">Growth</div><h1 className="serif text-[28px]">Outreach</h1><div className="text-sm text-[var(--muted)]">{formatINR(totalRevenue)} attributed to partners</div></div>
        <button className="btn-gold" onClick={() => setEdit({ target_name: '', category: 'photographer', stage: 'not_contacted' })} data-testid="outreach-add"><Plus size={14}/> Add</button>
      </div>
      <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
        <button onClick={() => setFilter('all')} className="px-3 py-1 text-xs rounded-full whitespace-nowrap" style={{background: filter==='all'?'var(--charcoal)':'transparent', color: filter==='all'?'var(--cream)':'var(--charcoal)', border:'1px solid var(--line)'}}>All</button>
        {STAGES.map(s => <button key={s} onClick={() => setFilter(s)} className="px-3 py-1 text-xs rounded-full whitespace-nowrap tracking-[0.14em] uppercase" style={{background: filter===s?'var(--charcoal)':'transparent', color: filter===s?'var(--cream)':'var(--charcoal)', border:'1px solid var(--line)'}}>{s.replace('_',' ')}</button>)}
      </div>
      <div className="space-y-2">
        {filtered.length === 0 && <div className="card-cream p-8 text-center text-sm text-[var(--muted)] italic">No targets in this stage.</div>}
        {filtered.map(o => {
          const tpl = TEMPLATES.find(t => t.key === (o.stage==='not_contacted' ? (o.category==='planner'?'cold_planner':'cold_photographer') : o.stage==='emailed' ? 'followup_day4' : 'followup_day10'));
          const contact = contacts.find(c => c.id === o.contact_id);
          const msg = tpl ? tpl.body : '';
          return (
            <div key={o.id} className="card-cream p-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="serif text-[17px]">{o.target_name}</div>
                  <div className="text-xs text-[var(--muted)] mt-1"><span className="uppercase tracking-[0.14em]">{o.category}</span>{o.city?` · ${o.city}`:''}{o.next_followup?` · follow-up ${formatDate(o.next_followup)}`:''}</div>
                </div>
                <span className={`status-pill status-${o.stage==='collaborated'?'ok':o.stage==='not_interested'?'overdue':o.stage.startsWith('followup')?'pending':'draft'}`}>{o.stage.replace('_',' ')}</span>
              </div>
              <div className="flex gap-2 mt-3 flex-wrap">
                {o.stage !== 'collaborated' && o.stage !== 'not_interested' && <button className="btn-ghost text-xs" onClick={() => advance(o)} data-testid={`outreach-advance-${o.id}`}>Advance stage</button>}
                {contact?.phone && <a href={waLink(contact.phone, msg)} target="_blank" rel="noreferrer" className="btn-ghost text-xs"><MessageCircle size={12}/> WhatsApp</a>}
                <button className="btn-ghost text-xs" onClick={() => setEdit(o)}>Edit</button>
                <button className="text-[var(--overdue)] text-xs" onClick={() => rm(o.id)}><Trash2 size={12}/></button>
              </div>
            </div>
          );
        })}
      </div>
      {edit && (
        <div className="fixed inset-0 z-50 flex items-end lg:items-center justify-center bg-black/40 p-0 lg:p-6">
          <div className="w-full max-w-md card-cream p-6 space-y-3">
            <div className="flex items-center justify-between"><h2 className="serif text-[22px]">Outreach Target</h2><button onClick={() => setEdit(null)}><X size={18}/></button></div>
            <div><label className="eyebrow">Name</label><input value={edit.target_name} onChange={e => setEdit({...edit, target_name: e.target.value})}/></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="eyebrow">Category</label><select value={edit.category} onChange={e => setEdit({...edit, category: e.target.value})}>{CATS.map(c => <option key={c}>{c}</option>)}</select></div>
              <div><label className="eyebrow">City</label><input value={edit.city||''} onChange={e => setEdit({...edit, city: e.target.value})}/></div>
              <div><label className="eyebrow">Stage</label><select value={edit.stage} onChange={e => setEdit({...edit, stage: e.target.value})}>{STAGES.map(s => <option key={s} value={s}>{s.replace('_',' ')}</option>)}</select></div>
              <div><label className="eyebrow">Contact</label><select value={edit.contact_id||''} onChange={e => setEdit({...edit, contact_id: e.target.value})}><option value="">—</option>{contacts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select></div>
              <div><label className="eyebrow">Last contacted</label><input type="date" value={edit.last_contacted||''} onChange={e => setEdit({...edit, last_contacted: e.target.value})}/></div>
              <div><label className="eyebrow">Next follow-up</label><input type="date" value={edit.next_followup||''} onChange={e => setEdit({...edit, next_followup: e.target.value})}/></div>
              <div><label className="eyebrow">Revenue attributed (₹)</label><input type="number" value={edit.revenue_attributed||0} onChange={e => setEdit({...edit, revenue_attributed: parseFloat(e.target.value||0)})}/></div>
            </div>
            <div><label className="eyebrow">Notes</label><textarea rows={2} value={edit.notes||''} onChange={e => setEdit({...edit, notes: e.target.value})}/></div>
            <button className="btn-gold w-full" onClick={() => save(edit)} data-testid="outreach-save">Save</button>
          </div>
        </div>
      )}
    </div>
  );
}
